import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";

import { JobListRequest, JobRead } from "@nens/rana/api-v1";
import { getRanaApiV1 } from "@nens/rana/api-v1";

import { ProjectContext } from "@/stores/project-provider";
import { jobIsRunning } from "@/utils/job-is-running";

import { createItemUpdaterForQueryLists } from "./hooks/create-item-updater-for-query-list";
import { updateIfOutdated } from "./hooks/if-outdated";
import { queryDirectly } from "./hooks/query-directly";

// Single

const getJobQuery = (jobId?: string) => {
  return getRanaApiV1().getQueryOptions({
    queryKey: ["project-job", "stat", jobId],
    queryFn: async () => {
      if (!jobId) throw Error("Never happens");
      return await getRanaApiV1().JobsApi.jobRetrieve({ jobId });
    },
    refetchInterval: (e) => jobIsRunning(e.state.data?.state.type) && 2000,
    refetchOnMount: (e) => (jobIsRunning(e.state.data?.state.type) ? "always" : true),
    enabled: !!jobId,
  });
};

export const useGetJobQuery = () => {
  return getJobQuery;
};

export const useJobQueryDirectly = () => {
  const queryClient = useQueryClient();
  return async (jobId?: string) => {
    return await queryDirectly(queryClient, getJobQuery(jobId));
  };
};

export const useJobQueries = (jobIds: string[]) => {
  return useQueries({ queries: jobIds.map(getJobQuery) });
};

// LIST

type JobListRequestArgs = Omit<JobListRequest, "tenantId"> & {
  enabled?: boolean;
  disableRefetching?: boolean;
};

const getJobListQuery = ({ enabled, disableRefetching, ...params }: JobListRequestArgs) => {
  const { projectId, ...otherParams } = params;

  return getRanaApiV1().getQueryOptions({
    queryKey: ["project-jobs", "list", projectId, otherParams],
    queryFn: async () => await getRanaApiV1().JobsApi.jobList(params),
    refetchInterval: (e) =>
      e.state.data?.items.some((job) => jobIsRunning(job?.state.type)) && 5000,
    refetchOnMount: (e) =>
      e.state.data?.items.some((job) => jobIsRunning(job?.state.type)) ? "always" : true,
    enabled,
    ...(disableRefetching && { refetchInterval: false, refetchOnMount: false }),
  });
};

export const useJobListQuery = (params?: Omit<JobListRequestArgs, "projectId">) => {
  const { id: projectId } = useContext(ProjectContext);
  return useQuery(getJobListQuery({ ...params, projectId }));
};

// INVALIDATION

export const useInvalidateJobListQuery = () => {
  const queryClient = useQueryClient();
  const { id: projectId } = useContext(ProjectContext);

  return async () => {
    await queryClient.invalidateQueries({
      queryKey: getJobListQuery({ projectId }).queryKey.slice(0, -1),
      refetchType: "all",
    });
  };
};

export const useUpdateJobInListsQuery = () => {
  const { id: projectId } = useContext(ProjectContext);
  const queryClient = useQueryClient();

  return createItemUpdaterForQueryLists(queryClient, {
    getQueryKey: () => getJobListQuery({ projectId }).queryKey.slice(0, -1),
  });
};

export const useUpdateJobInListIfOutdated = () => {
  const queryClient = useQueryClient();
  const { id: projectId } = useContext(ProjectContext);
  const queryKey = getJobListQuery({ projectId }).queryKey.slice(0, -1);

  const updateJobInListsQuery = createItemUpdaterForQueryLists(queryClient, {
    getQueryKey: () => queryKey,
  });

  return async (finishedAt: Date, job: JobRead) => {
    updateIfOutdated(queryClient, finishedAt, queryKey, () => updateJobInListsQuery(job.id, job));
  };
};
